import { motion } from 'framer-motion';
import { Globe, Twitter, Instagram, Linkedin, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useState } from 'react';
import { Link } from 'react-router-dom';

export const Footer = ({ onOpenLegal, onOpenContact }) => {
  const { t, language, changeLanguage, languageNames, availableLanguages } = useLanguage();
  const [isLangOpen, setIsLangOpen] = useState(false);

  const socialLinks = [
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  return (
    <footer className="relative bg-[#0A0A0A] overflow-hidden" data-testid="footer">
      {/* Large Background Text */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none select-none">
        <motion.h2
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 0.03, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="font-montserrat font-black text-[20vw] leading-none text-white translate-y-1/3"
        >
          JOINZY
        </motion.h2>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <motion.a
              href="#"
              whileHover={{ scale: 1.02 }}
              className="inline-block mb-6"
              data-testid="footer-logo"
            >
              <span className="font-montserrat font-black text-3xl tracking-tight text-[#008080]">
                Joinzy
              </span>
            </motion.a>
            <p className="font-poppins text-white/50 max-w-md mb-8 leading-relaxed">
              {t('footer.tagline')}
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 hover:bg-[#008080]/30 text-white/50 hover:text-[#00E5FF] transition-colors"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-montserrat font-semibold text-white mb-6">{t('footer.legal')}</h3>
            <ul className="space-y-4">
              <li>
                <Link
                  to="/subscription"
                  className="font-poppins text-white/50 hover:text-[#00E5FF] transition-colors"
                  data-testid="footer-subscription-link"
                >
                  {t('footer.subscription')}
                </Link>
              </li>
              <li>
                <button
                  onClick={onOpenLegal}
                  className="font-poppins text-white/50 hover:text-[#00E5FF] transition-colors"
                  data-testid="footer-privacy-link"
                >
                  {t('footer.privacy')}
                </button>
              </li>
              <li>
                <Link
                  to="/termsandconditions"
                  className="font-poppins text-white/50 hover:text-[#00E5FF] transition-colors"
                  data-testid="footer-terms-link"
                >
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact-us"
                  className="font-poppins text-white/50 hover:text-[#00E5FF] transition-colors"
                  data-testid="footer-contact-link"
                >
                  {t('footer.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Language Selector */}
          <div>
            <h3 className="font-montserrat font-semibold text-white mb-6">{t('footer.language')}</h3>
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white/5 rounded-xl text-white/70 hover:bg-white/10 transition-colors font-poppins"
                data-testid="footer-language-trigger"
              >
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span>{languageNames[language]}</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute bottom-full left-0 right-0 mb-2 bg-[#1A1A1A] border border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto"
                  data-testid="footer-language-menu"
                >
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        changeLanguage(lang);
                        setIsLangOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm font-poppins transition-colors ${
                        language === lang
                          ? 'bg-[#008080]/30 text-[#00E5FF]'
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                      data-testid={`footer-language-option-${lang}`}
                    >
                      {languageNames[lang]}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-poppins text-sm text-white/40" data-testid="footer-copyright">
              {t('footer.copyright')}
            </p>
            <p className="font-mono text-xs text-white/30 uppercase tracking-wider">
              {t('footer.madeWith')}
            </p>
          </div>
        </div>
      </div>

      {/* Decorative Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(to right, #008080 1px, transparent 1px),
                           linear-gradient(to bottom, #008080 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />
      </div>
    </footer>
  );
};
